/**
 * DeleteWatchlistDialog Container Component
 *
 * Manages delete confirmation dialog state including close-during-deletion
 * protection. Replaces Alert.alert() which doesn't render on web.
 *
 * Renders the shared ConfirmDialog molecule (SE-5524); the
 * former bespoke iOS-prompt chrome (raw Pressable footer buttons) is
 * intentionally standardized onto the molecule's Button atoms. Native
 * keeps `useRNModal` so the dialog stacks over parent native modals.
 * @module features/scouting/components/DeleteWatchlistDialog
 */

import { useCallback } from "react";
import { Platform } from "react-native";

import ConfirmDialog from "@/components/molecules/ConfirmDialog";

/** Props for DeleteWatchlistDialogContainer */
export interface DeleteWatchlistDialogProps {
  /** Whether the dialog is visible */
  readonly isOpen: boolean;
  /** Whether deletion is in progress */
  readonly isDeleting: boolean;
  /** Name of the watchlist to delete */
  readonly watchlistName: string;
  /** Callback invoked when the dialog is dismissed */
  readonly onClose: () => void;
  /** Callback invoked when user confirms deletion */
  readonly onConfirmDelete: () => void;
}

/**
 * Container for the delete watchlist confirmation dialog
 * @param root0 - Component props
 * @param root0.isOpen - Whether the dialog is visible
 * @param root0.isDeleting - Whether deletion is in progress
 * @param root0.watchlistName - Name of the watchlist to delete
 * @param root0.onClose - Close handler
 * @param root0.onConfirmDelete - Confirm deletion handler
 */
const DeleteWatchlistDialogContainer = ({
  isOpen,
  isDeleting,
  watchlistName,
  onClose,
  onConfirmDelete,
}: DeleteWatchlistDialogProps) => {
  const handleClose = useCallback(() => {
    if (!isDeleting) {
      onClose();
    }
  }, [isDeleting, onClose]);

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={onConfirmDelete}
      title="Delete List"
      body={`Are you sure you want to delete “${watchlistName}”? This action cannot be undone.`}
      tone="danger"
      confirmLabel={isDeleting ? "Deleting..." : "Delete List"}
      isConfirming={isDeleting}
      // Capability gating (not layout): native RN Modal stacking so the
      // dialog renders above parent native modals.
      useRNModal={Platform.OS !== "web"}
      size="sm"
      testID="delete-watchlist-dialog"
    />
  );
};

export default DeleteWatchlistDialogContainer;
